<script setup lang="ts">
  import { MpContextMenu, MpText } from '@borg/ui';

  const props = withDefaults(
    defineProps<{
      title?: string;
      subtitle?: string | null;
      from?: Date | null;
      to?: Date | null;
      description?: string;
      datesFormat?: 'monthYear' | 'year';
      showDuration?: boolean;
      disableToTime?: boolean;
      preview?: boolean;
      isEditable?: boolean;
      isDeletable?: boolean;
      test?: string;
    }>(),
    {
      title: undefined,
      subtitle: undefined,
      from: undefined,
      to: undefined,
      description: undefined,
      datesFormat: 'monthYear',
      showDuration: false,
      disableToTime: false,
      isEditable: true,
      isDeletable: true,
      test: undefined,
    },
  );

  const emits = defineEmits<{
    (eventName: 'edit'): void;
    (eventName: 'delete'): void;
  }>();

  const i18n = useI18n();

  const duration = computed(() => {
    if (!props.from) {
      return undefined;
    }
    const end = props.to ?? new Date();

    const differenceDays = Math.max(
      0,
      Math.round((end.getTime() - props.from.getTime()) / 1000 / 60 / 60 / 24),
    );

    const differenceYears = Math.floor(differenceDays / 365);
    const differenceAdditionalMonths = Math.floor((differenceDays - differenceYears * 365) / 30);

    const durationParts = [];
    if (differenceYears) {
      durationParts.push(`${differenceYears}${i18n.t('YEARS_SHORT')}`);
    }
    if (differenceAdditionalMonths) {
      durationParts.push(`${differenceAdditionalMonths}${i18n.t('MONTHS_SHORT')}`);
    }
    if (durationParts.length === 0) {
      durationParts.push(`${differenceDays}${i18n.t('DAYS_SHORT')}`);
    }

    const fromString = i18n.d(...localeDateTimeFormat(props.from, props.datesFormat));
    const toString = props.to
      ? i18n.d(...localeDateTimeFormat(props.to, props.datesFormat))
      : i18n.t('IN_PROGRESS');

    const toStringFinal = props.disableToTime ? '' : ` - ${toString}`;
    const durationStringFinal = props.showDuration ? ` (${durationParts.join(', ')})` : '';

    if (!differenceDays) {
      return `${fromString}${toStringFinal}`;
    }

    return `${fromString}${toStringFinal}${durationStringFinal}`;
  });

  const options = computed(() => {
    const options = [];
    if (props.isEditable) {
      options.push({ label: i18n.t('EDIT'), onClick: () => emits('edit') });
    }
    if (props.isDeletable) {
      options.push({ label: i18n.t('DELETE'), onClick: () => emits('delete') });
    }
    return options;
  });
</script>

<template>
  <div
    :class="{
      'my-profile-entry': true,
      'my-profile-entry--preview': preview,
    }"
  >
    <div class="my-profile-entry__content">
      <MpText
        v-if="title"
        as="h6"
        style="margin: var(--mp-space-20) 0"
      >
        {{ title }}
      </MpText>
      <MpText
        v-if="subtitle"
        as="div"
        regular
        style="margin: var(--mp-space-20) 0"
      >
        {{ subtitle }}
      </MpText>
      <MpText
        v-if="duration"
        as="div"
        regular
        shade="40"
        style="margin: var(--mp-space-20) 0"
      >
        {{ duration }}
      </MpText>
      <MpText
        v-if="description"
        type="body2"
        as="div"
        no-margin
        shade="40"
        style="margin-top: var(--mp-space-40)"
      >
        {{ description }}
      </MpText>
    </div>
    <div
      v-if="!preview && options.length"
      class="my-profile-entry__options"
    >
      <MpContextMenu
        :test="`${title}-${test}`"
        :options="options"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .my-profile-entry {
    $self: &;

    padding: var(--mp-space-40) 0;
    display: flex;
    page-break-inside: avoid;

    &:hover {
      background-color: var(--mp-color-grey-200);

      --padding: var(--mp-space-50);
      width: calc(100% + 2 * var(--padding));
      padding-left: var(--padding);
      padding-right: var(--padding);
      position: relative;
      left: calc(-1 * var(--padding));

      @include screen-md {
        --padding: var(--mp-space-70);
      }

      @include dark-mode {
        background-color: var(--mp-color-grey-800);
      }
    }

    &__content {
      flex: 2;
      display: flex;
      flex-direction: column;
      justify-content: center;

      & > :first-child {
        margin-top: 0 !important;
      }

      & > :last-child {
        margin-bottom: 0 !important;
      }
    }

    &--preview {
      margin-bottom: var(--mp-space-60);

      padding: 0;

      &:hover {
        position: static;
        width: inherit;
        padding: 0;
        background-color: inherit;
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    &__options {
      margin: auto 0 auto var(--mp-space-70);
    }
  }
</style>
